import { useSearchParams, usePathname } from 'next/navigation';
import { useCategories } from '@/features/select-category/model/hooks';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useGameApi } from '@/entities/game/model/api';
import { useNotifications } from '@/shared/providers/notifications-provider/notifications-context';

export function useGameSections() {
  const { user } = useUser();
  const { notifications, isNotifyLoading } = useNotifications();

  const { categories, isLoading: isCategoriesLoading } = useCategories();
  const { gameData, isGamesLoading, recommendedData, isRecommendedLoading } =
    useGameApi();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Получаем параметры
  const filterParam = searchParams.get('filter');
  const searchParam = searchParams.get('search');

  // Извлекаем gameTypeOrProvider из URL (регулярка)
  const gameTypeOrProvider = pathname.match(/\/games\/([^\/]+)/)?.[1];
  const validGameTypes = ['slots', 'instant', 'table', 'live'];

  // Определяем тип фильтра
  const isTypeSelected = validGameTypes.includes(gameTypeOrProvider);
  const isWishlist = gameTypeOrProvider === 'favorite';
  const isProviderSelected =
    gameTypeOrProvider && !isTypeSelected && !isWishlist;
  const isCategorySelected = filterParam && filterParam !== 'all';
  const isSearchTyped = searchParam && searchParam !== '';

  // Загрузка данных
  if (
    isNotifyLoading ||
    isCategoriesLoading ||
    isGamesLoading ||
    isRecommendedLoading
  ) {
    return {
      isSectionsLoading: true,
      sections: null,
    };
  }
  const recommendedGame = gameData?.filter((el) =>
    recommendedData?.recommended?.includes(el?.id)
  );
  const preferredGame = gameData?.filter((el) =>
    recommendedData?.preferred?.includes(el?.id)
  );
  const newGame = gameData?.filter((el) => el.filters.includes('new'));

  // Основной фильтр (тип ИЛИ провайдер)
  const filterByTypeOrProvider = (game) => {
    if (isTypeSelected) return game.type === gameTypeOrProvider;
    if (isProviderSelected)
      return (
        gameTypeOrProvider === 'all' || game.providerId === gameTypeOrProvider
      );
    return true;
  };

  // Фильтр по категории (бонус/новые/популярные)
  const filterByCategory = (game) => {
    if (!isCategorySelected) return true;
    if (filterParam === 'bonus' && notifications?.bonus?.isActive) {
      return notifications?.bonusInfo?.games?.includes(game?.id);
    }
    return game?.filters?.includes(filterParam);
  };

  // Остальные фильтры
  const filterBySearch = (game) =>
    !isSearchTyped ||
    game.title.toLowerCase().includes(searchParam.toLowerCase());

  const filterByWishlist = (game) =>
    !isWishlist || user?.wishlist?.includes(game.id);

  // Формируем категории с играми
  const categoriesWithGames = categories.map((category) => {
    const isBonusCategory = category?.id === 'bonus';
    const filteredGames = isBonusCategory
      ? gameData.filter((el) => notifications?.bonusInfo?.games.includes(el.id))
      : gameData.filter(
          (game) =>
            game?.filters?.includes(category.id) &&
            filterByTypeOrProvider(game) &&
            filterBySearch(game) &&
            filterByWishlist(game)
        );
    return {
      ...category,
      games: filteredGames,
    };
  });

  // Финальные отфильтрованные игры
  const filteredGames = gameData.filter(
    (game) =>
      filterByTypeOrProvider(game) &&
      filterByCategory(game) &&
      filterBySearch(game) &&
      filterByWishlist(game)
  );

  // Показывать категории только если нет активных фильтров
  const showCategories =
    !isTypeSelected &&
    !isProviderSelected &&
    !isCategorySelected &&
    !isSearchTyped &&
    !isWishlist;

  return {
    categoriesWithGames,
    showCategories,
    games: filteredGames,
    gamesData: gameData,
    isSectionsLoading: false,
    isGamesLoading,
    isRecommendedLoading,
    recommendedGame,
    preferredGame,
    newGame,
  };
}
